﻿import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Modal, Paper } from '@mui/material';
import SelectSignatureType from './SelectSignatureType.tsx';
import { SIGNATURE_TYPE } from '~/constants/signatureTypes.ts';
import { DiiaQRCode, TimerRefHandle } from '~/components/SignatureVerification/DiiaQRCode';
import IITSign from '~/components/IITSign/IITSign.tsx';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { resetIITLib } from '~/libs/iit/lib.js';
import { dismissIITSigning } from '~/store/iit/slices.ts';
import { TIITSign } from '~/store/iit/types.ts';
import { infoMessages } from '~/constants/infoMessages';
import { TDocument } from '~/modules/identification/forms/Documents/types.ts';
import { LoadingQRCode } from './LoadingQRCode.tsx';
import { electronicSignatureStyles as styles } from './styles';

interface ElectronicSignatureProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  isDiiaSignatureDisabled?: boolean;
  diiaDisabledMessage?: string;
  isIITSignatureDisabled?: boolean;
  handleSignatureDiia?: () => void | boolean | Promise<void | boolean>;
  handleSignatureIIT?: () => void;
  onIITSign: (signFiles: TIITSign[]) => void;
  handleTimeUp?: () => void;
  handlePreview?: (id: number | string) => void;
  deepLink?: string;
  documents?: TDocument[];
  waitForDiiaInitialization?: boolean;
  resetCounter?: number;
  enableIITDocumentsPagination?: boolean;
  iitDocumentsPerPage?: number;
}

export const ElectronicSignature = forwardRef<TimerRefHandle, ElectronicSignatureProps>(
  (
    {
      isModalOpen,
      handleCloseModal,
      handleSignatureDiia,
      handleSignatureIIT,
      handleTimeUp,
      handlePreview,
      deepLink,
      onIITSign,
      documents,
      waitForDiiaInitialization = false,
      isDiiaSignatureDisabled = false,
      diiaDisabledMessage,
      isIITSignatureDisabled = false,
      resetCounter = 0,
      enableIITDocumentsPagination = false,
      iitDocumentsPerPage,
    }: ElectronicSignatureProps,
    timerRef
  ) => {
    const [selectedSignature, setSelectedSignature] = useState<SIGNATURE_TYPE | null>(null);
    const [step, setStep] = useState(1);
    const [isDiiaInitializing, setIsDiiaInitializing] = useState(false);
    const [iitDocumentsPage, setIITDocumentsPage] = useState(1);
    const previousResetCounterRef = useRef(resetCounter);
    const internalTimerRef = useRef<TimerRefHandle>(null);
    const pendingTimerStartRef = useRef(false);
    const dispatch = useAppDispatch();
    const allIITDocuments = documents ?? [];
    const iitPageSize = Math.max(1, iitDocumentsPerPage || allIITDocuments.length);
    const iitDocumentsPagesCount = Math.max(1, Math.ceil(allIITDocuments.length / iitPageSize));
    const startIndex = (iitDocumentsPage - 1) * iitPageSize;
    const visibleIITDocuments = enableIITDocumentsPagination
      ? allIITDocuments.slice(startIndex, startIndex + iitPageSize)
      : allIITDocuments;
    const iitPagination = enableIITDocumentsPagination
      ? {
          page: iitDocumentsPage,
          pagesCount: iitDocumentsPagesCount,
          onPageChange: setIITDocumentsPage,
        }
      : undefined;

    const preventQrCodeTimer =
      step !== 2 || selectedSignature !== SIGNATURE_TYPE.DIIA || !pendingTimerStartRef.current;

    const startQrTimer = () => {
      if (internalTimerRef.current) {
        pendingTimerStartRef.current = false;
        internalTimerRef.current.startTimer();
        return;
      }

      pendingTimerStartRef.current = true;
    };

    const resetQrTimer = () => {
      pendingTimerStartRef.current = false;
      internalTimerRef.current?.resetTimer();
    };

    useImperativeHandle(
      timerRef,
      () => ({
        startTimer: startQrTimer,
        resetTimer: resetQrTimer,
      }),
      []
    );

    const resetModalState = () => {
      setStep(1);
      setSelectedSignature(null);
      setIsDiiaInitializing(false);
      setIITDocumentsPage(1);
      resetQrTimer();
      dispatch(dismissIITSigning());
      resetIITLib();
    };

    const onDiiaSignature = async () => {
      if (isDiiaInitializing) return;

      if (waitForDiiaInitialization) {
        setIsDiiaInitializing(true);
        const isInitialized = await handleSignatureDiia?.();
        setIsDiiaInitializing(false);

        if (isInitialized === false) {
          return;
        }
      } else {
        handleSignatureDiia?.();
      }

      setStep(2);
      setSelectedSignature(SIGNATURE_TYPE.DIIA);
    };

    useEffect(() => {
      setStep(1);
      setSelectedSignature(null);
      setIsDiiaInitializing(false);
    }, [isModalOpen]);

    useEffect(() => {
      if (previousResetCounterRef.current === resetCounter) {
        return;
      }

      previousResetCounterRef.current = resetCounter;
      setStep(1);
      setSelectedSignature(null);
      setIsDiiaInitializing(false);
      setIITDocumentsPage(1);
      resetQrTimer();
      dispatch(dismissIITSigning());
      resetIITLib();
    }, [dispatch, resetCounter]);

    useEffect(() => {
      if (!enableIITDocumentsPagination) {
        return;
      }

      if (iitDocumentsPage > iitDocumentsPagesCount) {
        setIITDocumentsPage(iitDocumentsPagesCount);
      }
    }, [enableIITDocumentsPagination, iitDocumentsPage, iitDocumentsPagesCount]);

    useEffect(() => {
      if (preventQrCodeTimer) return;
      startQrTimer();
    }, [step, selectedSignature]);

    const handleClose = () => {
      handleCloseModal();
      resetModalState();
    };

    const handleModalClose = (_event: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
      handleClose();
    };

    const onIITSignature = () => {
      handleSignatureIIT?.();
      setStep(2);
      setSelectedSignature(SIGNATURE_TYPE.IIT);
    };

    const getModalView = (currentStep: number) => {
      switch (currentStep) {
        case 1:
          if (isDiiaInitializing) {
            return <LoadingQRCode />;
          }

          return (
            <SelectSignatureType
              handleSignatureDiia={onDiiaSignature}
              handleSignatureIIT={onIITSignature}
              isDiiaSignatureDisabled={isDiiaSignatureDisabled || isDiiaInitializing}
              isIITSignatureDisabled={isIITSignatureDisabled || isDiiaInitializing}
              diiaDisabledMessage={diiaDisabledMessage}
            />
          );
        case 2:
          if (selectedSignature === SIGNATURE_TYPE.DIIA)
            return (
              <DiiaQRCode
                deepLink={deepLink}
                timerRef={internalTimerRef}
                qrCodeTimer={180}
                onTimeUp={handleTimeUp}
                diiaTitle={'Підписати документи через Дію'}
                instructionText={infoMessages.FOLLOW_DIIA_SIGN_INSTRUCTIONS}
              />
            );
          if (selectedSignature === SIGNATURE_TYPE.IIT)
            return (
              <IITSign
                signDocs={onIITSign}
                documents={visibleIITDocuments}
                handlePreview={handlePreview}
                pagination={iitPagination}
              />
            );
          return null;
        default:
          return null;
      }
    };

    return (
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        disableEscapeKeyDown
        data-testid="electronic-signature-modal"
      >
        <Paper sx={styles.modalPaper}>{getModalView(step)}</Paper>
      </Modal>
    );
  }
);
